// src/components/Features/ISO27001BCPSelector.tsx - BCP Selection for ISO 27001 DRP
import React, { useState, useEffect } from 'react';
import { getUserBCPs, BCP } from '../../services/api';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';
import ISO27001Modal from '../BCP/ISO27001Modal';

interface ISO27001BCPSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

const ISO27001BCPSelector: React.FC<ISO27001BCPSelectorProps> = ({ isOpen, onClose }) => {
  const [bcps, setBcps] = useState<BCP[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedBCPId, setSelectedBCPId] = useState<number | null>(null);
  const [selectedBCPName, setSelectedBCPName] = useState<string>('');
  const [showISO27001Modal, setShowISO27001Modal] = useState(false);

  const { canAccessFeature } = useSubscriptionContext();
  const { user } = useAuth();

  // Check if user has access (premium subscription or admin)
  const hasAccess = canAccessFeature('iso_27001_readiness') ||
                    (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'));

  useEffect(() => {
    if (isOpen) {
      if (!hasAccess) {
        setShowUpgradeModal(true);
      } else {
        loadBCPs();
      }
    }
  }, [isOpen, hasAccess]);

  const loadBCPs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUserBCPs();
      setBcps(data);
    } catch (err) {
      console.error('Error loading BCPs:', err);
      setError('Failed to load your BCP plans. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBCPSelect = (bcpId: number, bcpName: string) => {
    setSelectedBCPId(bcpId);
    setSelectedBCPName(bcpName);
    setShowISO27001Modal(true);
  };

  const handleISO27001ModalClose = () => {
    setShowISO27001Modal(false);
    setSelectedBCPId(null);
    setSelectedBCPName('');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">ISO 27001 Disaster Recovery Plan</h2>
                  <p className="text-sm text-gray-600">Select a BCP to view its ISO 27001 DRP</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {!hasAccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Plan Required</h3>
                  <p className="text-gray-600 mb-6">
                    ISO 27001 Disaster Recovery Plan is available for Premium subscribers.
                  </p>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200"
                  >
                    Upgrade to Premium Plan
                  </button>
                </div>
              ) : isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-purple-600 animate-spin"></div>
                    <p className="text-gray-600">Loading your BCP plans...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading BCPs</h3>
                  <p className="text-gray-600 mb-6">{error}</p>
                  <button
                    onClick={loadBCPs}
                    className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : bcps.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No BCP Plans Found</h3>
                  <p className="text-gray-600 mb-6">
                    You need to create a BCP plan before generating an ISO 27001 Disaster Recovery Plan.
                  </p>
                  <button
                    onClick={() => {
                      onClose();
                      window.location.href = '/bcp/new';
                    }}
                    className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Create BCP Plan
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Instructions */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-1">Select a BCP Plan</h4>
                        <p className="text-sm text-purple-800">
                          Choose the Business Continuity Plan you want to view an ISO 27001 Disaster Recovery Plan for.
                          The plan will include comprehensive disaster recovery strategies, risk assessments, and compliance information.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* BCP List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bcps.map((bcp) => (
                      <button
                        key={bcp.id}
                        onClick={() => handleBCPSelect(bcp.id, bcp.business_name)}
                        className="text-left border-2 border-gray-200 rounded-lg p-4 hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 group"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 group-hover:text-purple-900 mb-1">
                              {bcp.business_name}
                            </h3>
                            <p className="text-sm text-gray-600">{bcp.industry}</p>
                          </div>
                          <div className="ml-3 flex-shrink-0">
                            <div className={`px-2 py-1 text-xs rounded ${
                              bcp.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : bcp.status === 'in_review'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {bcp.status === 'completed' ? 'Completed' :
                               bcp.status === 'in_review' ? 'In Review' : 'Draft'}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {bcp.business_type}
                            </span>
                            {bcp.employee_count && (
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {bcp.employee_count} employees
                              </span>
                            )}
                          </div>
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredFeature="premium"
        featureName="ISO 27001 Disaster Recovery Plan"
      />

      {/* ISO 27001 Modal */}
      {selectedBCPId && (
        <ISO27001Modal
          isOpen={showISO27001Modal}
          onClose={handleISO27001ModalClose}
          bcpId={selectedBCPId}
          bcpName={selectedBCPName}
        />
      )}
    </>
  );
};

export default ISO27001BCPSelector;

